import React, { useMemo } from "react";
import { useMemoizedFn } from "ahooks";
import { IonSpinner } from "@ionic/react";
import { useTranslation } from "react-i18next";
import { BackButton } from "@core/components";
import MaxWidthContainer from "../../../components/UI/MaxWidthContainer/MaxWidthContainer";
import Page from "../../page-layout";
import {
  useAutoMarkChatRead,
  useEnsureOrderChatMutation,
  useOrderChatMessagesQuery,
  useOrderChatQuery,
  useSendOrderChatFileMutation,
  useSendOrderChatMessageMutation,
} from "../../../providers/Chat";
import MessagesList from "./components/MessagesList";
import ChatComposer from "./components/ChatComposer";

type RouteProps = { match: { params: { orderId: string } } };

const OrderChatPage = ({ match }: RouteProps) => {
  const { t } = useTranslation();
  const orderId = Number(match.params.orderId) || undefined;

  const { data: chat = null, isLoading: isChatLoading } = useOrderChatQuery(orderId);
  const messagesQuery = useOrderChatMessagesQuery(chat?.id);
  const ensureChat = useEnsureOrderChatMutation();
  const sendMessage = useSendOrderChatMessageMutation();
  const sendFile = useSendOrderChatFileMutation();

  const messages = useMemo(() => messagesQuery.data?.pages.flatMap((page) => page.items) ?? [], [messagesQuery.data]);

  useAutoMarkChatRead(chat?.id, messages[0], chat?.orderId);

  const isClosed = !!chat && chat.status !== "open";

  const getChatForSend = useMemoizedFn(async () => {
    if (chat) return chat;
    if (!orderId) return null;

    return ensureChat.mutateAsync(orderId);
  });

  const handleSendText = useMemoizedFn(async (text: string) => {
    try {
      const currentChat = await getChatForSend();
      if (!currentChat) return;

      sendMessage.mutate({ chatId: currentChat.id, text });
    } catch (error) {
      console.error("ensureOrderChat", error);
    }
  });

  const handleSendFile = useMemoizedFn(async (file: File) => {
    try {
      const currentChat = await getChatForSend();
      if (!currentChat) return;

      sendFile.mutate({ chatId: currentChat.id, file });
    } catch (error) {
      console.error("ensureOrderChat", error);
    }
  });

  const handleLoadMore = useMemoizedFn(() => {
    messagesQuery.fetchNextPage();
  });

  return (
    <Page>
      <Page.Header className="flex flex-col items-center justify-start mt-[env(safe-area-inset-top)] mb-2.5 border-b border-gray-300">
        <MaxWidthContainer classList="flex items-center justify-center bg-white w-full h-16">
          <div className="flex justify-center items-center w-full relative">
            <div className="absolute left-2.5">
              <BackButton />
            </div>
            <div className="font-extrabold">
              {t("Chat")} №{orderId}
            </div>
          </div>
        </MaxWidthContainer>
      </Page.Header>

      <Page.Content>
        {isChatLoading ? (
          <div className="flex justify-center items-center h-full">
            <IonSpinner />
          </div>
        ) : (
          <MaxWidthContainer classList="flex flex-col h-full">
            <MessagesList
              messages={messages}
              isFetchingNextPage={messagesQuery.isFetchingNextPage}
              hasNextPage={!!messagesQuery.hasNextPage}
              operatorLastReadAt={chat?.operatorLastReadAt ?? null}
              onLoadMore={handleLoadMore}
            />

            {isClosed && (
              <div className="px-3 py-2 text-center text-sm text-gray-500 bg-gray-100 border-t border-gray-200">
                {t("This chat is closed")}
              </div>
            )}

            <ChatComposer
              isClosed={isClosed}
              isSendingMessage={sendMessage.isPending || ensureChat.isPending}
              isSendingFile={sendFile.isPending || ensureChat.isPending}
              onSendText={handleSendText}
              onSendFile={handleSendFile}
            />
          </MaxWidthContainer>
        )}
      </Page.Content>
    </Page>
  );
};

export default OrderChatPage;
